import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import axios from "@/api/axios";
import { useNotificationStore } from "@/stores/notification";

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY;
const FCM_TOKEN_STORAGE_KEY = "admin_fcm_token";
const FOREGROUND_DEDUP_TTL = 15000;

function hasFirebaseConfig() {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId &&
      VAPID_KEY
  );
}

let messagingSupportPromise = null;
let serviceWorkerRegistrationPromise = null;
let foregroundListenerAttached = false;
let foregroundUnsubscribe = null;
let initPromise = null;

const recentForegroundMessages = new Map();

function hasWindowSupport() {
  return typeof window !== "undefined";
}

function hasServiceWorkerSupport() {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

function isAuthenticated() {
  return Boolean(localStorage.getItem("admin_token"));
}

function getFirebaseApp() {
  if (getApps().length > 0) {
    return getApp();
  }

  return initializeApp(firebaseConfig);
}

async function isMessagingSupported() {
  if (!hasWindowSupport() || !hasServiceWorkerSupport()) {
    return false;
  }

  if (!messagingSupportPromise) {
    messagingSupportPromise = isSupported().catch((error) => {
      console.warn("FCM support detection failed:", error);
      return false;
    });
  }

  return messagingSupportPromise;
}

async function getMessagingInstance() {
  const supported = await isMessagingSupported();

  if (!supported) {
    return null;
  }

  return getMessaging(getFirebaseApp());
}

async function registerMessagingServiceWorker() {
  if (!hasServiceWorkerSupport()) {
    return null;
  }

  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );
  }

  return serviceWorkerRegistrationPromise;
}

function getStoredFcmToken() {
  return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
}

function setStoredFcmToken(token) {
  localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
}

function clearStoredFcmToken() {
  localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
}

function cleanupRecentForegroundMessages() {
  const now = Date.now();

  recentForegroundMessages.forEach((timestamp, key) => {
    if (now - timestamp > FOREGROUND_DEDUP_TTL) {
      recentForegroundMessages.delete(key);
    }
  });
}

function buildForegroundMessageKey(payload) {
  const data = payload?.data || {};

  return (
    payload?.messageId ||
    data.message_id ||
    data.notification_id ||
    data.id ||
    [data.title || "", data.body || "", data.created_at || ""].join("|")
  );
}

function isDuplicateForegroundMessage(payload) {
  cleanupRecentForegroundMessages();

  const key = buildForegroundMessageKey(payload);

  if (!key) {
    return false;
  }

  if (recentForegroundMessages.has(key)) {
    return true;
  }

  recentForegroundMessages.set(key, Date.now());
  return false;
}

function extractNotificationContent(payload) {
  const data = payload?.data || {};

  return {
    title: data.title || "Ridey Admin",
    body: data.body || "",
    icon: data.icon || "/favicon.ico",
    tag:
      data.tag ||
      data.notification_id ||
      data.id ||
      payload?.messageId ||
      undefined,
    data,
  };
}

function showForegroundNotification(payload) {
  if (!hasWindowSupport() || !("Notification" in window)) {
    return;
  }

  if (document.visibilityState !== "visible") {
    return;
  }

  if (Notification.permission !== "granted") {
    return;
  }

  const notificationContent = extractNotificationContent(payload);

  if (!notificationContent.title && !notificationContent.body) {
    return;
  }

  new Notification(notificationContent.title, {
    body: notificationContent.body,
    icon: notificationContent.icon,
    tag: notificationContent.tag,
    data: notificationContent.data,
  });
}

async function syncAdminFcmToken(token) {
  if (!token) {
    return;
  }

  const currentStoredToken = getStoredFcmToken();

  if (currentStoredToken === token) {
    return;
  }

  await axios.post("/fcm-token", {
    token,
    device_type: "web",
  });

  setStoredFcmToken(token);
}

async function requestNotificationPermission() {
  if (!hasWindowSupport() || !("Notification" in window)) {
    return "denied";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  if (Notification.permission === "denied") {
    return "denied";
  }

  return Notification.requestPermission();
}

async function handleForegroundMessage(payload) {
  if (isDuplicateForegroundMessage(payload)) {
    return;
  }

  const store = useNotificationStore();
  const refreshPromise = store.fetchNotifications().catch((error) => {
    console.error("Foreground notification refresh failed:", error);
    throw error;
  });

  showForegroundNotification(payload);

  await refreshPromise;
}

async function attachForegroundListener() {
  if (foregroundListenerAttached) {
    return foregroundUnsubscribe;
  }

  const messaging = await getMessagingInstance();

  if (!messaging) {
    return null;
  }

  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    handleForegroundMessage(payload).catch((error) => {
      console.error("Foreground message handling failed:", error);
    });
  });

  foregroundListenerAttached = true;

  return foregroundUnsubscribe;
}

export async function initAdminPushNotifications() {
  if (!isAuthenticated()) {
    return null;
  }

  if (!hasFirebaseConfig()) {
    console.warn(
      "FCM is disabled because the Firebase environment configuration is incomplete."
    );
    return null;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      const messaging = await getMessagingInstance();

      if (!messaging) {
        return null;
      }

      await attachForegroundListener();

      const permission = await requestNotificationPermission();

      if (permission !== "granted") {
        return null;
      }

      const serviceWorkerRegistration = await registerMessagingServiceWorker();

      if (!serviceWorkerRegistration) {
        return null;
      }

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration,
      });

      if (!token) {
        console.warn("No FCM token received.");
        return null;
      }

      await syncAdminFcmToken(token);

      return token;
    } catch (error) {
      console.error("FCM init error:", error);
      throw error;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

export async function clearAdminFcmToken() {
  const token = getStoredFcmToken();

  if (token && isAuthenticated()) {
    try {
      await axios.delete("/fcm-token", {
        data: { token },
      });
    } catch (error) {
      console.warn("Failed to remove FCM token from backend:", error);
    }
  }

  clearStoredFcmToken();
}

export function resetAdminPushNotifications() {
  if (typeof foregroundUnsubscribe === "function") {
    foregroundUnsubscribe();
  }

  foregroundUnsubscribe = null;
  foregroundListenerAttached = false;
  initPromise = null;
  recentForegroundMessages.clear();
}
